import { NavLink, useLocation } from "react-router-dom";
import type { NavItemConfig } from "@/types/navigation";
import { cn } from "@/lib/utils";
import { useNavBadge } from "@/hooks/useNavBadge";
import { useSidebar } from "@/contexts/SidebarContext";
import { useAdminReviewNotifications } from "@/hooks/useAdminReviewNotifications";
import { useUserRole } from "@/hooks/useUserRole";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface NavItemProps {
  item: NavItemConfig;
  onNavigate?: () => void;
  collapsed?: boolean;
  isInQuickActions?: boolean;
}

export function NavItem({ item, onNavigate, collapsed: collapsedProp, isInQuickActions = false }: NavItemProps) {
  const badge = useNavBadge(item.badge);
  const { isCollapsed: contextCollapsed } = useSidebar();
  const collapsed = collapsedProp ?? contextCollapsed;
  const { role } = useUserRole();
  const { counts: reviewCounts } = useAdminReviewNotifications();
  const isPrimary = item.variant === "primary";
  const location = useLocation();

  const isReviewQueue = item.href === "/admin/review-queue";
  const reviewQueueBadge =
    isReviewQueue && role === "admin" && reviewCounts.total > 0
      ? { show: true, value: reviewCounts.total > 9 ? "9+" : reviewCounts.total, variant: "destructive" as const }
      : { show: false, value: 0 };

  const displayBadge = isReviewQueue ? reviewQueueBadge : badge;

  const isActiveForLocation = () => {
    if (!item.href || item.href.startsWith("#")) return false;
    const currentPath = location.pathname;
    const currentSearch = location.search;
    const targetUrl = new URL(item.href, window.location.origin);
    const targetPath = targetUrl.pathname;
    const targetSearch = targetUrl.search;
    if (targetPath.includes("/contacts") || targetPath.includes("/customers")) {
      if (currentPath === targetPath) {
        if (targetSearch) return currentSearch === targetSearch;
        return !currentSearch || currentSearch === "";
      }
      return false;
    }
    const normCurrent = currentPath.replace(/\/$/, "") || "/";
    const normTarget = targetPath.replace(/\/$/, "") || "/";
    return normCurrent === normTarget;
  };

  const handleClick = () => {
    onNavigate?.();
  };

  const navLink = (
    <NavLink
      to={item.href}
      onClick={handleClick}
      end={true}
      aria-current={({ isActive }) => (isActive ? "page" : undefined)}
      aria-label={item.label}
      data-collapsed-nav={collapsed ? "true" : undefined}
      data-collapsed-active={collapsed && isActiveForLocation() ? "true" : undefined}
      className={({ isActive }) => {
        const isActiveState = isActiveForLocation();
        const collapsedBase =
          "flex items-center justify-center h-11 w-11 min-w-11 max-w-11 aspect-square box-border rounded-xl transition-all duration-150 cursor-pointer flex-shrink-0 !ring-0 outline-none focus:!ring-0 focus-visible:!ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background leading-none [&>*]:block [&>*]:shrink-0 [&>svg]:block";
        const collapsedInactive = "bg-white text-muted-foreground hover:bg-muted hover:text-foreground";
        const collapsedActive = "bg-primary text-primary-foreground [&>svg]:text-primary-foreground";
        return collapsed
          ? cn(collapsedBase, isActiveState ? collapsedActive : collapsedInactive)
          : cn(
              "flex items-center gap-4 w-full rounded-xl pl-3 pr-3 py-2.5 text-left text-sm font-medium",
              "transition-colors duration-150 transition-transform active:scale-[0.98]",
              isInQuickActions
                ? cn(
                    isPrimary
                      ? "bg-white/20 text-white font-semibold border border-white/30 hover:bg-white/30"
                      : "text-white font-medium hover:bg-white/10",
                    isActiveState && !isPrimary && "bg-white/15"
                  )
                : cn(
                    isActiveState
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                  ),
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            );
      }}
    >
      {({ isActive }) => {
        const isActiveState = isActiveForLocation();
        return (
          <>
            {!collapsed && (
              <>
                <item.icon
                  className={cn(
                    "flex-shrink-0 h-5 w-5",
                    isInQuickActions
                      ? "text-white"
                      : isActiveState
                        ? "text-primary-foreground"
                        : "text-muted-foreground"
                  )}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                {displayBadge.show && (
                  <span
                    key={String(displayBadge.value)}
                    className={cn(
                      "flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center text-xs font-semibold rounded-full tabular-nums animate-in fade-in-0 zoom-in-95 duration-200",
                      displayBadge.variant === "destructive"
                        ? isInQuickActions
                          ? "bg-destructive text-white"
                          : "bg-destructive/5 text-destructive"
                        : displayBadge.variant === "warning"
                          ? "bg-warning/5 text-warning"
                          : "bg-primary/10 text-primary"
                    )}
                  >
                    {displayBadge.value}
                  </span>
                )}
              </>
            )}
            {collapsed && (
              <span className="flex size-full items-center justify-center">
                <item.icon
                  className={cn(
                    "flex-shrink-0 h-5 w-5 shrink-0 block",
                    isActiveState ? "text-white" : "text-muted-foreground"
                  )}
                  aria-hidden
                />
              </span>
            )}
          </>
        );
      }}
    </NavLink>
  );

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{navLink}</TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={12}
            align="center"
            className="z-dropdown max-w-xs rounded-lg border-0 bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg"
          >
            <div className="flex items-center gap-2">
              <span>{item.label}</span>
              {displayBadge.show && (
                <span
                  className={cn(
                    "px-1.5 py-0.5 text-xs font-medium rounded-full",
                    displayBadge.variant === "destructive" ? "bg-destructive text-white" : "bg-white/20 text-white"
                  )}
                >
                  {displayBadge.value}
                </span>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return navLink;
}
